import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NewProject } from '../../../types/project';
import { BlurField, ValidationErrors } from '../types';

export const useValidation = (project: NewProject) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const validateField = useCallback(
    (name: keyof NewProject): string | undefined => {
      switch (name) {
        case 'name':
          return undefined;
        case 'description':
          return undefined;
        case 'tolerance': {
          if (
            project.tolerance === null ||
            project.tolerance === undefined ||
            project.tolerance.toString().trim() === ''
          ) {
            return t('projects.newProject.validation.toleranceRequired');
          }
          if (project.tolerance < 0 || project.tolerance > 1) {
            return t('projects.newProject.validation.toleranceRange');
          }
          return undefined;
        }
        case 'maintenance': {
          if (
            project.maintenance === null ||
            project.maintenance === undefined ||
            project.maintenance.toString().trim() === ''
          ) {
            return t('projects.newProject.validation.maintenanceRequired');
          }
          if (project.maintenance < 0 || project.maintenance > 1) {
            return t('projects.newProject.validation.maintenanceRange');
          }
          return undefined;
        }
        case 'latitude':
          return project.latitude < -90 || project.latitude > 90
            ? t('projects.newProject.validation.latitudeRange')
            : undefined;
        case 'longitude':
          return project.longitude < -180 || project.longitude > 180
            ? t('projects.newProject.validation.longitudeRange')
            : undefined;
        default:
          return undefined;
      }
    },
    [project, t],
  );
  const validateForm = useCallback((): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;
    const allTouched: Record<string, boolean> = {};
    Object.keys(project).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);
    Object.entries(project).forEach(([key]) => {
      const fieldName = key as keyof NewProject;
      const error = validateField(fieldName);
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    });
    setErrors(newErrors);
    return isValid;
  }, [project, validateField]);
  const handleBlur = (field: BlurField) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field);
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };
  return {
    errors,
    touched,
    setTouched,
    setErrors,
    validateField,
    validateForm,
    handleBlur,
  };
};
